import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { ROUTE_STATUS_LABEL, fmtDuration } from "@/lib/status";
import { Route as RouteIcon, Plus, Trash2, ArrowRight, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function RoutesList() {
  const [routes, setRoutes] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Supprimer cette route ?")) return;
    await api.delete(`/routes/${id}`); toast.success("Route supprimée"); load();
  };

  return (
    <div>
      <PageHeader title="Routes" subtitle="Gérer, analyser et optimiser les routes" icon={RouteIcon}
        actions={<Button onClick={() => nav("/routes/import")} data-testid="new-route-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle route</Button>} />
      <div className="p-8">
        {routes.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-border rounded-lg">
            <RouteIcon className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3 text-muted-foreground">Aucune route. Importez votre première route.</p>
            <Button className="mt-4" onClick={() => nav("/routes/import")}><Plus className="w-4 h-4 mr-1" /> Importer</Button>
          </div>
        ) : (
          <div className="grid gap-px bg-border border border-border rounded-lg overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 bg-secondary px-5 py-3 overline">
              <div className="col-span-3">Route</div><div className="col-span-2">Statut</div>
              <div className="col-span-2">Arrêts</div><div className="col-span-2">Distance</div>
              <div className="col-span-1">Sécurité</div><div className="col-span-2 text-right">Actions</div>
            </div>
            {routes.map((r) => {
              const m = r.metrics_optimized || r.metrics_original;
              const approvable = m ? m.approvable : null;
              return (
                <div key={r.id} className="grid grid-cols-2 md:grid-cols-12 gap-4 bg-card px-5 py-4 items-center" data-testid={`route-row-${r.route_number}`}>
                  <div className="col-span-2 md:col-span-3">
                    <div className="font-display font-bold">Route {r.route_number}</div>
                    <div className="text-xs text-muted-foreground">{r.school || r.name}</div>
                  </div>
                  <div className="md:col-span-2"><Badge variant="secondary">{ROUTE_STATUS_LABEL[r.status] || r.status}</Badge></div>
                  <div className="md:col-span-2 text-sm">{(r.original_stops || []).length} arrêts</div>
                  <div className="md:col-span-2 text-sm font-mono">{m ? `${m.distance_km} km` : "—"}<span className="text-muted-foreground"> · {m ? fmtDuration(m.duration_min) : ""}</span></div>
                  <div className="md:col-span-1">
                    {approvable === null ? <span className="text-slate-300">—</span> :
                      approvable ? <ShieldCheck className="w-5 h-5 text-emerald-500" /> : <ShieldAlert className="w-5 h-5 text-red-500" />}
                  </div>
                  <div className="col-span-2 md:col-span-2 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`del-${r.route_number}`}><Trash2 className="w-4 h-4" /></Button>
                    <Button size="sm" asChild data-testid={`analyze-${r.route_number}`}>
                      <Link to={`/routes/${r.id}/analyze`}>Analyser <ArrowRight className="w-4 h-4 ml-1" /></Link>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
